import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, MessageCircle, Send, Sparkles, Users } from 'lucide-react';
import Button from '../components/common/Button';
import { useTranslation } from 'react-i18next';

const heroShellClass = 'relative overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 text-gray-950 shadow-sm dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white dark:shadow-xl';
const heroGlowClass = 'absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-amber-400/15 dark:from-primary-500/30 dark:via-accent-500/25 dark:to-amber-400/10';
const heroChipClass = 'mb-2 inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white/80 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:shadow-none';
const heroInfoPanelClass = 'rounded-2xl border border-primary-100 bg-white/75 px-3 py-2 text-gray-950 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-white dark:shadow-none';

const Contact = () => {
  const { t } = useTranslation();

  const communityLinks = [
    {
      title: t('contact.group_title', 'Join the Support Group'),
      description: t('contact.group_desc', 'Need community help or status updates? Join our dedicated Telegram support group for real-time announcements and pinned guides.'),
      button: t('contact.open_group_btn', 'Open Telegram Group'),
      url: 'https://t.me/pshmodsgroup',
      icon: Users,
      tone: 'from-primary-500 to-secondary-500',
    },
    {
      title: t('contact.channel_title', 'Join PSH Mods Channel'),
      description: t('contact.channel_desc', 'Stay updated with the latest news, announcements, and releases by joining our official Telegram channel.'),
      button: t('contact.open_channel_btn', 'Open Telegram Channel'),
      url: 'https://t.me/pshmodsofficial',
      icon: Send,
      tone: 'from-accent-500 to-amber-500',
    },
  ];

  const accounts = [
    {
      label: t('contact.support_label'),
      handle: 'https://telegram.me/pshsupport',
      description: t('contact.support_desc'),
      badge: 'Support',
    },
    {
      label: t('contact.enquiries_label'),
      handle: 'https://t.me/D4_D3F4AULT',
      description: t('contact.enquiries_desc'),
      badge: 'Enquiry',
    },
  ];

  const openTelegram = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto max-w-5xl px-3 sm:px-4">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <section className={heroShellClass}>
            <div className={heroGlowClass} />
            <div className="relative p-4 sm:p-5">
              <div className={heroChipClass}>
                <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                Support Hub
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h1 className="text-2xl font-black tracking-normal sm:text-3xl">
                    {t('contact.title', 'Contact PSH Store')}
                  </h1>
                  <p className="mt-1 max-w-2xl text-sm font-medium text-gray-600 dark:text-gray-300">
                    {t('contact.subtitle', 'Reach the right Telegram contact for fast assistance. Responses are typically within a few hours.')}
                  </p>
                </div>
                <div className={heroInfoPanelClass}>
                  <p className="text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">Primary</p>
                  <p className="text-sm font-black text-gray-950 dark:text-white notranslate" translate="no">@pshsupport</p>
                </div>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {communityLinks.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.url} className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${item.tone} text-white shadow-sm`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <h2 className="text-base font-black text-gray-900 dark:text-white">{item.title}</h2>
                  <p className="mt-1 line-clamp-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {item.description}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openTelegram(item.url)}
                    className="mt-4 rounded-2xl"
                  >
                    {item.button}
                  </Button>
                </div>
              );
            })}
          </section>

          <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-primary-600 dark:text-primary-400">Direct Message</p>
                <h2 className="mt-1 text-lg font-black text-gray-900 dark:text-white">Telegram Contacts</h2>
              </div>
              <MessageCircle className="h-5 w-5 text-primary-500" />
            </div>

            <div className="grid gap-3">
              {accounts.map((account) => (
                <div
                  key={account.handle}
                  className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        {account.badge}
                      </span>
                      <h3 className="truncate text-sm font-black text-gray-900 dark:text-white">{account.label}</h3>
                    </div>
                    <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">{account.description}</p>
                    <p className="mt-1 text-xs font-black text-primary-600 dark:text-primary-400 notranslate" translate="no">
                      @{account.handle.split('/').pop()}
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => openTelegram(account.handle)}
                    className="rounded-2xl"
                  >
                    {t('contact.message_btn', 'Message on Telegram')}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
